import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './stores/auth.store';
import { RouteGuard } from './components/rbac/RouteGuard';
import { GoogleConnectRequiredOutlet } from './components/bookings/GoogleCalendarConnect';

// Auth pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ResendVerificationPage = lazy(() => import('./pages/ResendVerificationPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Layout
const AppShell = lazy(() => import('./components/layout/AppShell'));
const SuspendedOverlay = lazy(() => import('./components/layout/SuspendedOverlay'));

// Dashboard
const DashboardPage = lazy(() => import('./pages/DashboardPage'));

// Attendance
const AttendanceRecordPage = lazy(() => import('./pages/AttendanceRecordPage'));
const AttendanceReportPage = lazy(() => import('./pages/AttendanceReportPage'));
const AttendanceDayReportPage = lazy(() => import('./pages/AttendanceDayReportPage'));
const AttendanceSummaryReportPage = lazy(() => import('./pages/AttendanceSummaryReportPage'));
const AttendanceApprovalsPage = lazy(() => import('./pages/AttendanceApprovalsPage'));
const WhosWorkingPage = lazy(() => import('./pages/WhosWorkingPage'));
const WorkdayCalendarPage = lazy(() => import('./pages/WorkdayCalendarPage'));

// Leave
const WorkTrackerTimesheetsPage = lazy(() => import('./pages/WorkTrackerTimesheetsPage'));
const WorkTrackerActivityPage = lazy(() => import('./pages/WorkTrackerActivityPage'));
const WorkTrackerSettingsPage = lazy(() => import('./pages/WorkTrackerSettingsPage'));
const AppClassificationsPage = lazy(() => import('./pages/AppClassificationsPage'));
const AiUsagePage = lazy(() => import('./pages/AiUsagePage'));

const LeaveRequestsPage = lazy(() => import('./pages/LeaveRequestsPage'));
const LeaveApprovalPage = lazy(() => import('./pages/LeaveApprovalPage'));
const LeaveTypesPage = lazy(() => import('./pages/LeaveTypesPage'));
const LeaveBalancePage = lazy(() => import('./pages/LeaveBalancePage'));

// Profile
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

// User Management
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const UserGroupsPage = lazy(() => import('./pages/UserGroupsPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage'));
const SettingsModulesPage = lazy(() => import('./pages/SettingsModulesPage'));

// Payroll
const PayrollPage = lazy(() => import('./pages/PayrollPage'));
const PayrollDetailPage = lazy(() => import('./pages/PayrollDetailPage'));
const PayslipDetailPage = lazy(() => import('./pages/PayslipDetailPage'));
const MyPayslipsPage = lazy(() => import('./pages/MyPayslipsPage'));
const TaxRatesPage = lazy(() => import('./pages/TaxRatesPage'));
const TaxCertificatePage = lazy(() => import('./pages/TaxCertificatePage'));

// Funds (generic fund system — PF, EOBI, gratuity, etc.)
const FundsPage = lazy(() => import('./pages/FundsPage'));
const FundBalancesPage = lazy(() => import('./pages/FundBalancesPage'));
const FundLedgerHistoryPage = lazy(() => import('./pages/FundLedgerHistoryPage'));
const MyFundsPage = lazy(() => import('./pages/MyFundsPage'));

// Allowance / Deduction catalogs (per-org with group / per-user enrolment)
const AllowancesPage = lazy(() => import('./pages/AllowancesPage'));
const DeductionsPage = lazy(() => import('./pages/DeductionsPage'));
const MyAllowancesPage = lazy(() => import('./pages/MyAllowancesPage'));
const MyDeductionsPage = lazy(() => import('./pages/MyDeductionsPage'));

// Loans + Salary increment history
const LoansPage = lazy(() => import('./pages/LoansPage'));
// Recruitment, onboarding & offboarding (qa/spec/recruitment.md)
const RecruitmentJobsPage = lazy(() => import('./pages/recruitment/RecruitmentJobsPage'));
const PipelinePage = lazy(() => import('./pages/recruitment/PipelinePage'));
const ApplicantsPage = lazy(() => import('./pages/recruitment/ApplicantsPage'));
const ApplicantDetailPage = lazy(() => import('./pages/recruitment/ApplicantDetailPage'));
const InterviewsPage = lazy(() => import('./pages/recruitment/InterviewsPage'));
const InterviewDetailPage = lazy(() => import('./pages/recruitment/InterviewDetailPage'));
const InboxPage = lazy(() => import('./pages/recruitment/InboxPage'));
const RecruitmentSettingsPage = lazy(() => import('./pages/recruitment/RecruitmentSettingsPage'));
const OnboardingListPage = lazy(() => import('./pages/lifecycle/OnboardingListPage'));
const OnboardingDetailPage = lazy(() => import('./pages/lifecycle/OnboardingDetailPage'));
const OffboardingListPage = lazy(() => import('./pages/lifecycle/OffboardingListPage'));
const OffboardingDetailPage = lazy(() => import('./pages/lifecycle/OffboardingDetailPage'));
const MyTasksPage = lazy(() => import('./pages/lifecycle/MyTasksPage'));
const JobDetailPage = lazy(() => import('./pages/recruitment/JobDetailPage'));
const HiringRequestsPage = lazy(() => import('./pages/recruitment/HiringRequestsPage'));
const StageApprovalsPage = lazy(() => import('./pages/recruitment/StageApprovalsPage'));
const HiringRequestDetailPage = lazy(() => import('./pages/recruitment/HiringRequestDetailPage'));
const CareersPage = lazy(() => import('./pages/careers/CareersPage'));
const GoogleReturnPage = lazy(() => import('./pages/GoogleReturnPage'));
const LoanDetailPage = lazy(() => import('./pages/LoanDetailPage'));
const MyLoansPage = lazy(() => import('./pages/MyLoansPage'));
const SalaryHistoryPage = lazy(() => import('./pages/SalaryHistoryPage'));

// Resource booking
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const BookingApprovalPage = lazy(() => import('./pages/BookingApprovalPage'));
const BookingCalendarPage = lazy(() => import('./pages/BookingCalendarPage'));
const MeetingPlannerPage = lazy(() => import('./pages/MeetingPlannerPage'));
const ApprovalSettingsPage = lazy(() => import('./pages/ApprovalSettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/IntegrationsSettingsPage'));

// Reviews
const ReviewQuestionsPage = lazy(() => import('./pages/ReviewQuestionsPage'));
const ReviewProcessesPage = lazy(() => import('./pages/ReviewProcessesPage'));
const ReviewProcessDetailPage = lazy(() => import('./pages/ReviewProcessDetailPage'));
const MyReviewsPage = lazy(() => import('./pages/MyReviewsPage'));
const ReviewFormPage = lazy(() => import('./pages/ReviewFormPage'));
const ReviewSummaryPage = lazy(() => import('./pages/ReviewSummaryPage'));
const ReviewsManagerListPage = lazy(() => import('./pages/ReviewsManagerListPage'));
const ReviewSummaryIndexPage = lazy(() => import('./pages/ReviewSummaryIndexPage'));
const ReviewProcessSummaryListPage = lazy(() => import('./pages/ReviewProcessSummaryListPage'));
const MyTeamReviewsPage = lazy(() => import('./pages/MyTeamReviewsPage'));

// Billing
const BillingPage = lazy(() => import('./pages/BillingPage'));

// CMS & FAQ
const PagesPage = lazy(() => import('./pages/PagesPage'));
const PagePreviewPage = lazy(() => import('./pages/PagePreviewPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));

// Org Chart
const OrgChartPage = lazy(() => import('./pages/OrgChartPage'));

// Settings & Admin
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ActivityLogsPage = lazy(() => import('./pages/ActivityLogsPage'));
const OfficeLocationsPage = lazy(() => import('./pages/OfficeLocationsPage'));
const DeviceFleetPage = lazy(() => import('./pages/DeviceFleetPage'));
const DeviceAuditPage = lazy(() => import('./pages/DeviceAuditPage'));
const ScreenShareAuditPage = lazy(() => import('./pages/ScreenShareAuditPage'));
const MobileDevicesPage = lazy(() => import('./pages/MobileDevicesPage'));
const NotificationAutomationsPage = lazy(() => import('./pages/NotificationAutomationsPage'));
const OfficeMapPage = lazy(() => import('./pages/OfficeMapPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const CelebrationsPage = lazy(() => import('./pages/CelebrationsPage'));
const AttendanceReviewPage = lazy(() => import('./pages/AttendanceReviewPage'));

// Admin Panel
const AdminShell = lazy(() => import('./components/layout/AdminShell'));
const AdminGuard = lazy(() => import('./components/auth/AdminGuard'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminOrganizationsPage = lazy(() => import('./pages/admin/AdminOrganizationsPage'));
const AdminOrganizationDetailPage = lazy(() => import('./pages/admin/AdminOrganizationDetailPage'));
const AdminPlansPage = lazy(() => import('./pages/admin/AdminPlansPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authChecked = useAuthStore((s) => s.authChecked);
  const mustChangePassword = useAuthStore((s) => s.user?.mustChangePassword);
  const location = useLocation();

  if (!authChecked) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Force first-login password change before allowing access to the rest of the app
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const loadUser = useAuthStore((s) => s.loadUser);
  const authChecked = useAuthStore((s) => s.authChecked);
  const suspended = useAuthStore((s) => s.suspended);

  useEffect(() => {
    if (!authChecked) {
      loadUser();
    }
  }, [authChecked, loadUser]);

  return (
    <>
      {suspended && (
        <Suspense fallback={null}>
          <SuspendedOverlay />
        </Suspense>
      )}
      <Toaster
        position="top-right"
        toastOptions={{
          unstyled: true,
          classNames: {
            toast:
              'flex items-start gap-3 w-full p-4 shadow-lg border-l-4 text-sm font-medium',
            title: 'text-sm font-semibold leading-snug',
            description: 'text-sm leading-snug opacity-90',
            success: 'bg-emerald-600 text-white border-emerald-800',
            error: 'bg-rose-600 text-white border-rose-800',
            warning: 'bg-amber-500 text-white border-amber-700',
            info: 'bg-sky-600 text-white border-sky-800',
            default: 'bg-stone-800 text-white border-stone-900',
            icon: 'text-white',
          },
        }}
      />
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        }
      >
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/resend-verification" element={<ResendVerificationPage />} />

          {/* Public careers page (qa/spec/recruitment.md §12) — no login */}
          <Route path="/careers/:slug" element={<CareersPage />} />
          <Route path="/careers/:slug/:code" element={<CareersPage />} />

          {/* Google Calendar connect popup lands here and closes itself — no app shell in a small window */}
          <Route path="/google-return" element={<GoogleReturnPage />} />

          {/* Protected routes with AppShell layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/change-password" element={<ChangePasswordPage />} />
            {/* Dashboard */}
            <Route path="/" element={<DashboardPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/403" element={<ForbiddenPage />} />

            {/* Attendance */}
            <Route path="/attendance/records" element={<RouteGuard perms={['attendance.view', 'attendance.view-subordinate', 'attendance.view-own']}><AttendanceRecordPage /></RouteGuard>} />
            <Route path="/attendance/review" element={<RouteGuard perms={['attendance.resolve-review', 'attendance.resolve-review-own']}><AttendanceReviewPage /></RouteGuard>} />
            <Route path="/attendance/report" element={<RouteGuard perms={['attendance.view', 'attendance.view-subordinate', 'attendance.view-own']}><AttendanceReportPage /></RouteGuard>} />
            <Route path="/attendance/day-report" element={<RouteGuard perms={['attendance.view', 'attendance.view-subordinate']}><AttendanceDayReportPage /></RouteGuard>} />
            <Route path="/attendance/summary-report" element={<RouteGuard perms={['attendance.view', 'attendance.view-subordinate']}><AttendanceSummaryReportPage /></RouteGuard>} />
            <Route path="/attendance/approvals" element={<RouteGuard perms={['attendance.manage', 'attendance.manage-subordinate', 'attendance.manage-attendance-requests', 'attendance.manage-attendance-requests-subordinate', 'attendance.manage-penalty-waivers', 'attendance.manage-penalty-waivers-subordinate']}><AttendanceApprovalsPage /></RouteGuard>} />
            <Route path="/attendance/approval" element={<Navigate to="/attendance/approvals?tab=edits" replace />} />
            <Route path="/attendance/waivers" element={<Navigate to="/attendance/approvals?tab=missing" replace />} />
            <Route path="/attendance/whos-working" element={<RouteGuard perms={['attendance.view', 'attendance.view-subordinate']}><WhosWorkingPage /></RouteGuard>} />
            <Route path="/attendance/calendar-settings" element={<RouteGuard perms="attendance.manage-workday-settings"><WorkdayCalendarPage /></RouteGuard>} />

            {/* Work Tracker */}
            <Route path="/work-tracker/timesheets" element={<RouteGuard perms={['tracker.view', 'tracker.view-subordinate', 'tracker.view-own']}><WorkTrackerTimesheetsPage /></RouteGuard>} />
            <Route path="/work-tracker/activity" element={<RouteGuard perms={['tracker.view', 'tracker.view-subordinate', 'tracker.view-own']}><WorkTrackerActivityPage /></RouteGuard>} />
            <Route path="/work-tracker/screen-view-history" element={<RouteGuard perms={['tracker.live-share', 'tracker.live-view', 'tracker.live-view-subordinate', 'tracker.manage-live-share']}><ScreenShareAuditPage /></RouteGuard>} />
            <Route path="/work-tracker/settings" element={<RouteGuard perms="tracker.manage"><WorkTrackerSettingsPage /></RouteGuard>} />
            <Route path="/work-tracker/classifications" element={<RouteGuard perms="tracker.manage"><AppClassificationsPage /></RouteGuard>} />
            <Route path="/ai-usage" element={<RouteGuard perms={['ai.view']}><AiUsagePage /></RouteGuard>} />

            {/* Leave */}
            <Route path="/leave/requests" element={<LeaveRequestsPage />} />
            <Route
              path="/leave/approval"
              element={
                <RouteGuard
                  perms={[
                    'attendance.manage-leave-requests',
                    'attendance.manage-leave-requests-subordinate',
                    'attendance.manage-leave-requests-forwarded',
                  ]}
                >
                  <LeaveApprovalPage />
                </RouteGuard>
              }
            />
            <Route path="/leave/types" element={<RouteGuard perms="attendance.manage-leave-types"><LeaveTypesPage /></RouteGuard>} />
            <Route
              path="/leave/balance"
              element={
                <RouteGuard
                  perms={[
                    'attendance.view-leave-balances',
                    'attendance.view-leave-balances-subordinate',
                    'attendance.view-leave-balances-own',
                    'attendance.manage-leave-balances',
                    'attendance.manage-leave-balances-subordinate',
                  ]}
                >
                  <LeaveBalancePage />
                </RouteGuard>
              }
            />

            {/* Profile */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/profile/:id"
              element={
                <RouteGuard perms={['user.view', 'user.view-subordinate']}>
                  <ProfilePage />
                </RouteGuard>
              }
            />

            {/* User Management */}
            <Route path="/employees" element={<RouteGuard perms={['user.view', 'user.manage']}><EmployeesPage /></RouteGuard>} />
            <Route path="/user-groups" element={<RouteGuard perms="user.manage-groups"><UserGroupsPage /></RouteGuard>} />
            <Route path="/roles" element={<RouteGuard perms="user.manage-roles"><RolesPage /></RouteGuard>} />
            <Route path="/org-chart" element={<RouteGuard perms={['user.view', 'user.view-subordinate']}><OrgChartPage /></RouteGuard>} />

            {/* Payroll */}
            <Route path="/payroll" element={<RouteGuard perms={['accounting.view-payroll', 'accounting.manage-payroll']}><PayrollPage /></RouteGuard>} />
            <Route path="/payroll/tax-certificate" element={<RouteGuard perms="accounting.view-payslips-own"><TaxCertificatePage /></RouteGuard>} />
            <Route path="/payroll/:id" element={<RouteGuard perms={['accounting.view-payroll', 'accounting.manage-payroll']}><PayrollDetailPage /></RouteGuard>} />
            <Route path="/payslips/me" element={<RouteGuard perms="accounting.view-payslips-own"><MyPayslipsPage /></RouteGuard>} />
            <Route path="/payslips/:id" element={<RouteGuard perms={['accounting.view-payslips', 'accounting.view-payslips-own', 'accounting.manage-payslips']}><PayslipDetailPage /></RouteGuard>} />
            <Route path="/tax-rates" element={<RouteGuard perms="accounting.view-tax"><TaxRatesPage /></RouteGuard>} />

            {/* Funds */}
            <Route path="/funds" element={<RouteGuard perms={['accounting.view-funds', 'accounting.manage-funds']}><FundsPage /></RouteGuard>} />
            <Route path="/funds/balances" element={<RouteGuard perms="accounting.view-fund-ledger"><FundBalancesPage /></RouteGuard>} />
            <Route path="/funds/:fundId/ledger/:userId" element={<RouteGuard perms={['accounting.view-fund-ledger', 'accounting.manage-funds']}><FundLedgerHistoryPage /></RouteGuard>} />
            <Route path="/funds/me" element={<RouteGuard perms="accounting.view-funds-own"><MyFundsPage /></RouteGuard>} />

            {/* Allowances + Deductions catalogs */}
            <Route path="/allowances" element={<RouteGuard perms={['accounting.view-allowances', 'accounting.manage-allowances']}><AllowancesPage /></RouteGuard>} />
            <Route path="/allowances/me" element={<RouteGuard perms="accounting.view-allowances-own"><MyAllowancesPage /></RouteGuard>} />
            <Route path="/deductions" element={<RouteGuard perms={['accounting.view-deductions', 'accounting.manage-deductions']}><DeductionsPage /></RouteGuard>} />
            <Route path="/deductions/me" element={<RouteGuard perms="accounting.view-deductions-own"><MyDeductionsPage /></RouteGuard>} />

            {/* Recruitment, onboarding & offboarding */}
            <Route path="/recruitment" element={<RouteGuard perms="recruitment.view-recruitment"><RecruitmentJobsPage /></RouteGuard>} />
            <Route path="/recruitment/jobs/:id" element={<RouteGuard perms="recruitment.view-recruitment"><JobDetailPage /></RouteGuard>} />
            {/* Anyone may be a named approver — the page shows only what the API lets them see. */}
            <Route path="/recruitment/requests" element={<HiringRequestsPage />} />
            <Route path="/recruitment/approvals" element={<StageApprovalsPage />} />
            <Route path="/recruitment/requests/:id" element={<HiringRequestDetailPage />} />
            <Route path="/recruitment/pipeline" element={<RouteGuard perms="recruitment.view-recruitment"><PipelinePage /></RouteGuard>} />
            <Route path="/recruitment/applicants" element={<RouteGuard perms="recruitment.view-recruitment"><ApplicantsPage /></RouteGuard>} />
            <Route path="/recruitment/applicants/:id" element={<RouteGuard perms={['recruitment.view-recruitment', 'recruitment.submit-interview-feedback']}><ApplicantDetailPage /></RouteGuard>} />
            <Route path="/recruitment/interviews" element={<RouteGuard perms={['recruitment.view-recruitment', 'recruitment.submit-interview-feedback']}><InterviewsPage /></RouteGuard>} />
            <Route path="/recruitment/interviews/:id" element={<RouteGuard perms={['recruitment.view-recruitment', 'recruitment.submit-interview-feedback']}><InterviewDetailPage /></RouteGuard>} />
            <Route path="/recruitment/inbox" element={<RouteGuard perms="recruitment.manage-recruitment"><InboxPage /></RouteGuard>} />
            <Route path="/recruitment/settings" element={<RouteGuard perms={['system.manage-settings']}><RecruitmentSettingsPage /></RouteGuard>} />
            <Route path="/onboarding" element={<RouteGuard perms="recruitment.view-onboarding"><OnboardingListPage /></RouteGuard>} />
            <Route path="/onboarding/:id" element={<RouteGuard perms="recruitment.view-onboarding"><OnboardingDetailPage /></RouteGuard>} />
            <Route path="/offboarding" element={<RouteGuard perms="recruitment.view-offboarding"><OffboardingListPage /></RouteGuard>} />
            <Route path="/offboarding/:id" element={<RouteGuard perms="recruitment.view-offboarding"><OffboardingDetailPage /></RouteGuard>} />
            <Route path="/my-tasks" element={<MyTasksPage />} />

            <Route path="/loans" element={<RouteGuard perms="accounting.manage-loans"><LoansPage /></RouteGuard>} />
            <Route path="/loans/me" element={<RouteGuard perms="accounting.view-loans-own"><MyLoansPage /></RouteGuard>} />
            <Route path="/loans/:id" element={<RouteGuard perms={['accounting.manage-loans', 'accounting.view-loans-own']}><LoanDetailPage /></RouteGuard>} />
            <Route path="/salary-history" element={<RouteGuard perms={['accounting.manage-salary-changes', 'accounting.view-salary-changes-own']}><SalaryHistoryPage /></RouteGuard>} />
            <Route path="/salary-history/:userId" element={<RouteGuard perms={['accounting.manage-salary-changes', 'accounting.view-salary-changes-own']}><SalaryHistoryPage /></RouteGuard>} />

            <Route path="/resources" element={<RouteGuard perms="resource.manage"><ResourcesPage /></RouteGuard>} />
            {/* My Bookings was folded into the room calendar (its filters live there now). */}
            {/* Bookings pages first ask for a connected Google Calendar when the org requires one. */}
            <Route element={<GoogleConnectRequiredOutlet />}>
              <Route path="/bookings" element={<Navigate to="/bookings/calendar?who=mine" replace />} />
              {/* Assigned room approvers need not hold approve-bookings (§C.11); the list is scoped server-side. */}
              <Route path="/bookings/approvals" element={<RouteGuard perms={['resource.book', 'resource.approve-bookings', 'resource.manage-bookings']}><BookingApprovalPage /></RouteGuard>} />
              <Route path="/bookings/approval-settings" element={<RouteGuard perms="resource.manage"><ApprovalSettingsPage /></RouteGuard>} />
              <Route path="/bookings/calendar" element={<RouteGuard perms={['resource.view', 'resource.book']}><BookingCalendarPage /></RouteGuard>} />
              <Route path="/bookings/planner" element={<RouteGuard perms="resource.book"><MeetingPlannerPage /></RouteGuard>} />
            </Route>
            <Route path="/settings/integrations" element={<RouteGuard perms="system.manage-integrations"><IntegrationsSettingsPage /></RouteGuard>} />

            {/* Reviews */}
            <Route
              path="/reviews/questions"
              element={
                <RouteGuard perms="hrm.view-review-questions">
                  <ReviewQuestionsPage />
                </RouteGuard>
              }
            />
            <Route
              path="/reviews/processes"
              element={
                <RouteGuard perms="hrm.view-review-processes">
                  <ReviewProcessesPage />
                </RouteGuard>
              }
            />
            <Route
              path="/reviews/processes/:id"
              element={
                <RouteGuard perms="hrm.view-review-processes">
                  <ReviewProcessDetailPage />
                </RouteGuard>
              }
            />
            <Route
              path="/reviews/pending"
              element={
                <RouteGuard perms={['hrm.view-review-assignments-own', 'hrm.submit-review', 'hrm.view-reviews-own']}>
                  <MyReviewsPage />
                </RouteGuard>
              }
            />
            <Route
              path="/reviews/:id"
              element={
                <RouteGuard
                  perms={[
                    'hrm.submit-review',
                    'hrm.view-reviews',
                    'hrm.view-reviews-subordinate',
                    'hrm.view-reviews-own',
                    'hrm.manage-review-processes',
                  ]}
                >
                  <ReviewFormPage />
                </RouteGuard>
              }
            />
            {/* Reviewed employees cannot view their own summary (the API denies it,
                and anonymisation is ineffective at small N). Only HR/admins and a
                subject's manager reach here — `hrm.view-reviews-own` deliberately dropped. */}
            <Route
              path="/reviews/summary/:processId/:employeeId"
              element={
                <RouteGuard perms={['hrm.view-reviews', 'hrm.view-reviews-subordinate']}>
                  <ReviewSummaryPage />
                </RouteGuard>
              }
            />
            {/* HR/manager-only audit list of all reviews across processes. */}
            <Route
              path="/reviews/manage"
              element={
                <RouteGuard perms={['hrm.view-reviews', 'hrm.view-reviews-subordinate']}>
                  <ReviewsManagerListPage />
                </RouteGuard>
              }
            />
            {/* Manager-facing: direct reports' reviews (moved off the HR process page). */}
            <Route
              path="/reviews/my-team"
              element={
                <RouteGuard perms={['hrm.view-reviews', 'hrm.view-reviews-subordinate']}>
                  <MyTeamReviewsPage />
                </RouteGuard>
              }
            />
            {/* Single-review drilldown — same form page in read-only mode. */}
            <Route
              path="/reviews/manage/:id"
              element={
                <RouteGuard perms={['hrm.view-reviews', 'hrm.view-reviews-subordinate']}>
                  <ReviewFormPage />
                </RouteGuard>
              }
            />
            {/* Process picker for summary entry. */}
            <Route
              path="/reviews/summary"
              element={
                <RouteGuard perms={['hrm.manage-review-summary', 'hrm.manage-review-summary-subordinate']}>
                  <ReviewSummaryIndexPage />
                </RouteGuard>
              }
            />
            {/* Reviewees list for one process. */}
            <Route
              path="/reviews/summary/:processId"
              element={
                <RouteGuard perms={['hrm.manage-review-summary', 'hrm.manage-review-summary-subordinate']}>
                  <ReviewProcessSummaryListPage />
                </RouteGuard>
              }
            />

            {/* Billing */}
            <Route path="/billing" element={<RouteGuard perms="system.view-billing"><BillingPage /></RouteGuard>} />

            {/* CMS & FAQ */}
            <Route path="/pages" element={<RouteGuard perms="cms.view-pages"><PagesPage /></RouteGuard>} />
            <Route path="/pages/:id/view" element={<RouteGuard perms="cms.view-pages"><PagePreviewPage /></RouteGuard>} />
            <Route path="/faq" element={<RouteGuard perms="cms.view-faqs"><FaqPage /></RouteGuard>} />

            {/* Settings & Admin */}
            <Route path="/settings" element={<RouteGuard perms="system.manage-settings"><SettingsPage /></RouteGuard>} />
            <Route path="/settings/modules" element={<RouteGuard perms="system.manage-modules"><SettingsModulesPage /></RouteGuard>} />
            <Route path="/settings/office-locations" element={<RouteGuard perms="system.manage-settings"><OfficeLocationsPage /></RouteGuard>} />
            <Route path="/settings/devices" element={<RouteGuard perms="attendance.manage"><DeviceFleetPage /></RouteGuard>} />
            <Route path="/settings/devices/audit" element={<RouteGuard perms="attendance.manage"><DeviceAuditPage /></RouteGuard>} />
            <Route path="/settings/mobile-devices" element={<RouteGuard perms={["system.manage-mobile-devices","system.view-mobile-devices","user.manage"]}><MobileDevicesPage /></RouteGuard>} />
            <Route
              path="/settings/notification-automations"
              element={<RouteGuard perms={['notification-automation.view', 'notification-automation.manage']}><NotificationAutomationsPage /></RouteGuard>}
            />
            <Route path="/office-map" element={<RouteGuard perms={['office-map.view', 'office-map.manage']}><OfficeMapPage /></RouteGuard>} />
            <Route path="/teams" element={<RouteGuard perms={['team.view', 'team.manage']}><TeamsPage /></RouteGuard>} />
            <Route path="/celebrations" element={<RouteGuard perms="hrm.view-celebrations"><CelebrationsPage /></RouteGuard>} />
            <Route path="/activity-logs" element={<RouteGuard perms="system.view-activity-logs"><ActivityLogsPage /></RouteGuard>} />
          </Route>

          {/* Admin Panel */}
          <Route
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminShell />
                </AdminGuard>
              </ProtectedRoute>
            }
          >
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
            <Route path="/admin/organizations/:id" element={<AdminOrganizationDetailPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
